import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import CustomModal from '~/components/CustomModal';
import EmptyResult from '~/components/EmptyResult';
import Footer from '~/components/Footer';
import Loader from '~/components/Loader';
import { ModalTypes } from '~/helpers/enums';
import { useJoinBroadcastHandler } from '~/hooks';
import { getSessionFromGuestLink, selectCurrentUserName } from '~/store';
import {
  getBroadcast,
  selectJoinBroadcastBroadcast,
  selectJoinBroadcastError,
  selectJoinBroadcastHost,
  selectJoinBroadcastPending,
} from '~/store/joinBroadcast';
import './JoinBroadcast.css';

const BROADCAST_LINK_ERROR_SUBTITLE = 'Please contact the host to confirm you were invited to the correct broadcast';
const GUEST_LINK_ERROR_SUBTITLE = 'Please contact the host or check your email for a new link';

/**
 * When called from a route with a guestHash (claim) the component will try to authenticate first and, following the
 * result of the authentication, redirect to the route without the guestHash path variable
 */
const JoinBroadcast = ({ checkAuthorization, trackEvent, appLogin }) => {
  const {
    params: { broadcastHash, guestHash },
  } = useRouteMatch();
  const dispatch = useDispatch();
  const isBroadcastLoading = useSelector(selectJoinBroadcastPending);
  const broadcast = useSelector(selectJoinBroadcastBroadcast);
  const host = useSelector(selectJoinBroadcastHost);
  const errorTitle = useSelector(selectJoinBroadcastError);
  const userName = useSelector(selectCurrentUserName);
  const [handleJoinBroadcast, isIncompatibleBrowser, closeModal] = useJoinBroadcastHandler({
    hash: broadcastHash,
    trackEvent,
    host,
  });

  useEffect(() => {
    const referrer = guestHash ? 'guest-link' : `join/${broadcastHash}`;
    checkAuthorization(referrer);
  }, [checkAuthorization, broadcastHash, guestHash]);

  const authorizeGuest = useCallback(async () => {
    const data = await dispatch(getSessionFromGuestLink({ broadcastHash, guestHash })).unwrap();
    if (data.authToken && typeof data.status === 'undefined') {
      await appLogin(data, false, `/join/${broadcastHash}`);
    }
  }, [broadcastHash, guestHash, dispatch, appLogin]);

  useEffect(() => {
    if (guestHash) {
      authorizeGuest();
      return;
    }
    dispatch(getBroadcast(broadcastHash));
  }, [broadcastHash, guestHash, dispatch, authorizeGuest]);

  if (isBroadcastLoading) {
    return (
      <div className="join-broadcast content-container">
        <Loader />
      </div>
    );
  }

  // IF not broadcast exist after loading is done. The user does not have access to the broadcast.
  if (!broadcast) {
    return (
      <>
        {guestHash && (
          <Link to="/" className="join-broadcast header-logo-container">
            <img src="/sl_logo_name_v2_light.png" className="header-logo" alt="logo" />
          </Link>
        )}
        <div className="join-broadcast content-container">
          <EmptyResult
            imgSrc="/no-guest-links.svg"
            title={errorTitle}
            message={guestHash ? GUEST_LINK_ERROR_SUBTITLE : BROADCAST_LINK_ERROR_SUBTITLE}
          />
        </div>
        {guestHash && <Footer />};
      </>
    );
  }

  return (
    <div className="join-broadcast content-container">
      <div className="broadcast-decoration">
        <div className="decoration-wrapper">
          <img src="/join-broadcast-placeholder.svg" alt="Broadcast decoration" />
          <p className="broadcasts-footnote">
            Join this broadcast using Chrome or Edge on desktop or on your iOS device using the mobile app.
          </p>
        </div>
      </div>
      <div className="broadcast-info">
        <p className="broadcast-copy">{userName}, you&apos;ve been invited to a Socialive broadcast</p>
        <h1 className="broadcast-title">{broadcast?.name}</h1>
        <p className="broadcasts-footnote">
          HOSTED BY <a href={`mailto:${host?.email}`}>{host?.name?.toUpperCase()}</a>
        </p>
        <button
          type="button"
          className="broadcast-join-button"
          data-broadcast-hash={broadcastHash}
          onClick={handleJoinBroadcast}
        >
          Join broadcast
        </button>
        <CustomModal
          handleOnClose={closeModal}
          includeButtons={false}
          isOpen={isIncompatibleBrowser}
          type={ModalTypes.INCOMPATIBLE_BROWSER}
        />
      </div>
    </div>
  );
};

JoinBroadcast.propTypes = {
  checkAuthorization: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  appLogin: PropTypes.func.isRequired,
};

export default JoinBroadcast;
